import { Box, Container, Typography, Button } from "@mui/material";
import { styled } from "@mui/material/styles";
import { translations } from "../translations";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";

const TermsContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "var(--background-white)",
  minHeight: "100vh",
  paddingTop: theme.spacing(18),
  paddingBottom: theme.spacing(8),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  [theme.breakpoints.down("sm")]: {
    paddingTop: theme.spacing(12.5),
  },
}));

const ContentContainer = styled(Container)(({ theme }) => ({
  width: "calc(100% - 16px)",
  maxWidth: "1200px",
  padding: "0 !important",
  wordWrap: "break-word",
  overflowWrap: "break-word",
  wordBreak: "break-word",
  "& h1": {
    marginBottom: theme.spacing(1.5),
    color: "var(--text-primary)",
    ...theme.typography.h2,
  },
  "& h2": {
    marginBottom: 0,
    color: "var(--text-primary)",
    ...theme.typography.h3,
  },
  "& p": {
    color: "var(--text-primary)",
    ...theme.typography.body1,
  },
  "& a": {
    color: "var(--text-primary)",
    textDecoration: "underline",
    "&:hover": {
      textDecoration: "underline",
    },
  },
}));

const BackButton = styled(Button)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  color: "var(--text-primary)",
  fontWeight: 400,
  textTransform: "none",
  ...theme.typography.body1,
  padding: 0,
  minWidth: "auto",
  "&:hover": {
    backgroundColor: "transparent",
  },
}));

const CreatedDate = styled(Typography)(({ theme }) => ({
  color: "var(--text-primary)",
  ...theme.typography.body1,
  marginBottom: theme.spacing(8),
}));

// Base card styles to avoid duplication
const BaseCard = styled(Box)(({ theme }) => ({
  backgroundColor: "var(--overlay-very-light)",
  borderRadius: "40px",
  padding: "40px",
  width: "calc(100%)",
  maxWidth: "1200px",
  wordWrap: "break-word",
  overflowWrap: "break-word",
  wordBreak: "break-word",
  display: "flex",
  flexDirection: "column",
  [theme.breakpoints.down("md")]: {
    padding: "24px",
    borderRadius: "24px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "16px",
    borderRadius: "16px",
    margin: "0 8px",
  },
}));

const SectionCard = styled(BaseCard)(({ theme }) => ({
  gap: theme.spacing(2), // 16px
}));

const SectionsContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  alignItems: "center",
}));


// Text components with shared styles
const TextBase = styled(Typography)(({ theme }) => ({
  color: "var(--text-primary)",
  ...theme.typography.body1,
  wordWrap: "break-word",
  overflowWrap: "break-word",
}));

const NoMarginText = styled(TextBase)(() => ({
  marginBottom: "0 !important",
}));

const IntroText = styled(TextBase)(({ theme }) => ({
  marginBottom: `${theme.spacing(4)} !important`,
}));

// Helper function for formatting text with bold text before a colon
const formatCompanyInfo = (text: string) => {
  const colonIndex = text.indexOf(":");
  if (colonIndex === -1) {
    return text;
  }

  const beforeColon = text.substring(0, colonIndex + 1);
  const afterColon = text.substring(colonIndex + 1);

  return (
    <>
      <strong style={{ fontWeight: 700 }}>{beforeColon}</strong>
      <span style={{ fontWeight: 400 }}>{afterColon}</span>
    </>
  );
};

export default function TermsAndConditions() {
  const t = translations;
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBackClick = () => {
    navigate("/");
  };

  // Функция для безопасного получения контента (только существующие поля)
  const getContent = (baseKey: string, maxCount: number = 10): string[] => {
    const content: string[] = [];
    for (let i = 1; i <= maxCount; i++) {
      const key = `${baseKey}Text${i}`;
      const value = (t as any)[key];
      if (value) {
        content.push(value);
      }
    }
    return content;
  };

  const sections = [
    {
      title: t.companyInfo,
      content: [
        t.companyName,
        t.companyRegistrationDate,
        t.companyAccreditation,
        t.companyLicense,
        t.companyTaxId,
        t.companyDomain,
        t.companyAddress,
      ],
    },
    {
      title: t.applicableLaw,
      content: [t.applicableLawText],
    },
    {
      title: t.conditionsOfUse,
      content: [t.conditionsOfUseText],
    },
    {
      title: t.privacyPolicySection,
      content: [t.privacyPolicyText],
    },
    {
      title: t.servicesProvided,
      content: [t.servicesProvidedText],
    },
    {
      title: t.paymentMethods,
      content: getContent("paymentMethods", 5),
    },
    {
      title: t.ageRestriction,
      content: [t.ageRestrictionText],
    },
    {
      title: t.authorizations,
      content: getContent("authorizations", 5),
    },
    {
      title: t.accountTransactionRecords,
      content: [
        t.accountTransactionRecordsText,
        (t as any).accountTransactionRecordsText2,
      ].filter(Boolean),
    },
    {
      title: t.security,
      content: getContent("security", 10),
    },
    {
      title: t.communications,
      content: getContent("communications", 5),
    },
    {
      title: t.intellectualProperty,
      content: getContent("intellectualProperty", 5),
    },
    {
      title: t.suspensionOfServices,
      content: [t.suspensionOfServicesText],
    },
    {
      title: t.disputes,
      content: [t.disputesText],
    },
    {
      title: t.indemnification,
      content: [t.indemnificationText],
    },
    {
      title: t.fees,
      content: [t.feesText],
    },
    {
      title: t.limitationOnLiability,
      content: getContent("limitationOnLiability", 5),
    },
    {
      title: t.reviewingTransactions,
      content: getContent("reviewingTransactions", 5),
    },
    {
      title: t.errorsAndResolutions,
      content: getContent("errorsAndResolutions", 5),
    },
    {
      title: t.paymentCancellation,
      content: getContent("paymentCancellation", 5),
    },
    {
      title: t.termination,
      content: getContent("termination", 10),
    },
    {
      title: t.changesToAgreement,
      content: getContent("changesToAgreement", 5),
    },
    {
      title: t.complianceInquiries,
      content: [t.complianceInquiriesText],
    },
  ];

  return (
    <TermsContainer>
      <ContentContainer>
        <BackButton onClick={handleBackClick}>{t.back}</BackButton>

        <Typography variant="h3" component="h1">
          {t.termsTitle}
        </Typography>

        <CreatedDate>{t.termsCreated}</CreatedDate>

        <IntroText variant="body1">{t.termsIntro}</IntroText>

        <SectionsContainer>
          {sections.map((section, index) =>
            index === 0 ? (
              <BaseCard key={index}>
                <Typography variant="h4" component="h2">
                  {section.title}
                </Typography>
                {section.content.map((paragraph, pIndex) => (
                  <NoMarginText key={pIndex} variant="body1">
                    {formatCompanyInfo(paragraph)}
                  </NoMarginText>
                ))}
              </BaseCard>
            ) : (
              <SectionCard key={index}>
                <Typography variant="h4" component="h2">
                  {section.title}
                </Typography>
                {section.content.map((paragraph, pIndex) => (
                  <NoMarginText key={pIndex} variant="body1">
                    {paragraph}
                  </NoMarginText>
                ))}
              </SectionCard>
            )
          )}
        </SectionsContainer>
      </ContentContainer>
    </TermsContainer>
  );
}
